import {
  Box,
  Button,
  Divider,
  HStack,
  Icon,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  VStack,
  useClipboard,
} from '@chakra-ui/react';
import React from 'react';
import { MdContentCopy, MdErrorOutline } from 'react-icons/md';
import { QRCodeSVG } from 'qrcode.react';
import { Label } from '@/components/atoms/Label';
import { PixFaturaResponse } from '../services/faturaApi';

interface PixModalProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
  pix: PixFaturaResponse | null;
  error: string | null;
  colors: { primaryColor: string };
}

function formatBRL(valor?: string | number | null) {
  if (valor === null || valor === undefined || valor === '') return null;
  const parsed = typeof valor === 'number' ? valor : Number(String(valor).replace(',', '.'));
  if (Number.isNaN(parsed)) return null;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parsed);
}

function formatExpiracao(value?: string | null) {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) return value;
  return `${match[3]}/${match[2]}/${match[1]}`;
}

function qrCodeSource(pix: PixFaturaResponse) {
  if (pix.pix_qrcode_base64) return `data:image/png;base64,${pix.pix_qrcode_base64}`;
  return pix.pix_qrcode_url || null;
}

class PixQrBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

export function PixModal({ isOpen, onClose, isLoading, pix, error, colors }: PixModalProps) {
  const { hasCopied, onCopy } = useClipboard(pix?.pix_copia_cola ?? '');

  const qrSource = pix ? qrCodeSource(pix) : null;
  const valorFormatado = formatBRL(pix?.valor);
  const expiracaoFormatada = formatExpiracao(pix?.expiracao_pix);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Pagar com Pix</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isLoading && (
            <VStack spacing={4} paddingY="40px">
              <Spinner color={colors.primaryColor} boxSize={10} thickness="3px" />
              <Label fontSize="sm" color="gray.600">
                Gerando o código Pix...
              </Label>
            </VStack>
          )}

          {!isLoading && error && (
            <VStack spacing={3} paddingY="30px" textAlign="center">
              <Icon as={MdErrorOutline} boxSize={10} color="#F59E0B" />
              <Label fontSize="sm" color="gray.700" lineHeight="1.4">
                {error}
              </Label>
            </VStack>
          )}

          {!isLoading && !error && pix && (
            <VStack spacing={4} align="stretch">
              {qrSource ? (
                <Box alignSelf="center" padding="12px" borderRadius="lg" border="1px solid" borderColor="gray.200">
                  <Image src={qrSource} alt="QR Code do Pix" boxSize="200px" objectFit="contain" />
                </Box>
              ) : (
                pix.pix_copia_cola && (
                  <PixQrBoundary key={pix.pix_copia_cola}>
                    <Box alignSelf="center" padding="12px" borderRadius="lg" border="1px solid" borderColor="gray.200" bg="white">
                      <QRCodeSVG value={pix.pix_copia_cola} size={200} level="M" />
                    </Box>
                  </PixQrBoundary>
                )
              )}

              {valorFormatado && (
                <Box
                  py={2.5}
                  px={3}
                  borderRadius="lg"
                  backgroundColor="gray.50"
                  border="1px solid"
                  borderColor="gray.100"
                >
                  <Label
                    fontSize="xs"
                    color="gray.500"
                    fontWeight="600"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    mb={1}
                  >
                    Valor a pagar
                  </Label>
                  <Label fontSize="xl" fontWeight="700" color={colors.primaryColor}>
                    {valorFormatado}
                  </Label>
                </Box>
              )}

              {(pix.pagador || pix.descricao) && (
                <VStack align="stretch" spacing={1.5}>
                  {pix.pagador && (
                    <HStack justify="space-between" align="flex-start" spacing={2}>
                      <Label fontSize="xs" color="gray.500" fontWeight="500" flexShrink={0}>
                        Pagador:
                      </Label>
                      <Label fontSize="sm" color="gray.800" fontWeight="600" textAlign="right" noOfLines={2}>
                        {pix.pagador}
                      </Label>
                    </HStack>
                  )}
                  {pix.descricao && (
                    <HStack justify="space-between" align="flex-start" spacing={2}>
                      <Label fontSize="xs" color="gray.500" fontWeight="500" flexShrink={0}>
                        Descrição:
                      </Label>
                      <Label fontSize="sm" color="gray.800" fontWeight="600" textAlign="right" noOfLines={3}>
                        {pix.descricao}
                      </Label>
                    </HStack>
                  )}
                </VStack>
              )}

              {pix.pix_copia_cola && (
                <>
                  <Divider />
                  <VStack align="stretch" spacing={2}>
                    <Label
                      fontSize="xs"
                      color="gray.500"
                      fontWeight="600"
                      textTransform="uppercase"
                      letterSpacing="wider"
                    >
                      Pix copia e cola
                    </Label>
                    <Box
                      padding="10px"
                      borderRadius="md"
                      backgroundColor="gray.50"
                      border="1px solid"
                      borderColor="gray.200"
                      fontFamily="mono"
                      fontSize="xs"
                      color="gray.600"
                      wordBreak="break-all"
                      noOfLines={3}
                    >
                      {pix.pix_copia_cola}
                    </Box>
                    <Button
                      leftIcon={<Icon as={MdContentCopy} />}
                      onClick={onCopy}
                      backgroundColor={colors.primaryColor}
                      color="white"
                      _hover={{ opacity: 0.9 }}
                    >
                      {hasCopied ? 'Copiado!' : 'Copiar código Pix'}
                    </Button>
                  </VStack>
                </>
              )}

              {expiracaoFormatada && (
                <Label fontSize="xs" color="gray.500" textAlign="center">
                  Validade: {expiracaoFormatada}
                </Label>
              )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" backgroundColor="transparent" color={colors.primaryColor} onClick={onClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
